import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { animate, motion, useInView, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Atom, Droplets, FlaskConical, Gift, Hourglass, Instagram, Leaf, Sparkles, Target, Waves } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard, { Stars } from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import Marquee from "@/components/Marquee";
import Particles, { GradientMesh } from "@/components/Particles";
import Reveal, { Magnetic, SectionHeading } from "@/components/Reveal";

const AYU_HERO = "https://images.unsplash.com/photo-1631730359585-38a4935cbec4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxhbWJlciUyMHNlcnVtJTIwYm90dGxlJTIwY29zbWV0aWMlMjBwYWNrYWdpbmclMjBsdXh1cnl8ZW58MHx8fHwxNzkwNzkwODI3fDA&ixlib=rb-4.1.0&q=85";
const AYU_IMG2 = "https://images.unsplash.com/photo-1608571423539-e951b9b3871e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhbWJlciUyMHNlcnVtJTIwYm90dGxlJTIwY29zbWV0aWMlMjBwYWNrYWdpbmclMjBsdXh1cnl8ZW58MHx8fHwxNzkwNzkwODI3fDA&ixlib=rb-4.1.0&q=85";
const SCI_HERO = "https://images.unsplash.com/photo-1777450793530-99a0e7b7fc53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwc2VydW0lMjBkcm9wcGVyJTIwbGFiJTIwc2tpbmNhcmUlMjBwYWNrYWdpbmd8ZW58MHx8fHwxNzkwNzkwODI3fDA&ixlib=rb-4.1.0&q=85";
const SCI_IMG2 = "https://images.unsplash.com/photo-1775620854129-ffebcc424f83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwc2VydW0lMjBkcm9wcGVyJTIwbGFiJTIwc2tpbmNhcmUlMjBwYWNrYWdpbmd8ZW58MHx8fHwxNzkwNzkwODI3fDA&ixlib=rb-4.1.0&q=85";
const LIFE_1 = "https://images.unsplash.com/photo-1557296387-5358ad7997bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxza2luY2FyZSUyMG1vZGVsJTIwcm91dGluZSUyMHdvbWFuJTIwcG9ydHJhaXQlMjBmYWNlfGVufDB8fHx8MTc5MDc5MDgzNHww&ixlib=rb-4.1.0&q=85";
const LIFE_2 = "https://images.unsplash.com/photo-1581182800629-7d90925ad072?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxza2luY2FyZSUyMG1vZGVsJTIwcm91dGluZSUyMHdvbWFuJTIwcG9ydHJhaXQlMjBmYWNlfGVufDB8fHx8MTc5MDc5MDgzNHww&ixlib=rb-4.1.0&q=85";
const ING_1 = "https://images.pexels.com/photos/5775023/pexels-photo-5775023.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const ING_2 = "https://images.unsplash.com/photo-1615485500834-bc10199bc727?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHx0dXJtZXJpYyUyMGhlcmJhbCUyMGJvdGFuaWNhbCUyMGluZ3JlZGllbnQlMjBjb3NtZXRpY3N8ZW58MHx8fHwxNzkwNzkwODM0fDA&ixlib=rb-4.1.0&q=85";

const DIWALI_DATE = new Date("2026-11-08T00:00:00+05:30");
const GIFT_SET_SLUG = "utsav-diwali-radiance-gift-set";

const CONCERNS = [
  { key: "acne", label: "Acne-Prone Skin", icon: Target, desc: "Clarify, calm, rebalance" },
  { key: "uneven-tone", label: "Uneven Tone", icon: Sparkles, desc: "Fade spots, even out" },
  { key: "dryness", label: "Dryness", icon: Droplets, desc: "Multi-depth hydration" },
  { key: "hair-care", label: "Hair Care", icon: Waves, desc: "Roots, shine, scalp" },
  { key: "aging", label: "Signs of Aging", icon: Hourglass, desc: "Firm, smooth, renew" },
];

const INGREDIENTS = [
  { name: "Kashmiri Crocus Saffron", origin: "Pampore, Kashmir", type: "Heritage Botanical", benefit: "Inhibits melanin synthesis and illuminates the complexion from within.", pairing: "Complemented by 10% Niacinamide", img: ING_2 },
  { name: "Pure Hyaluronic Acid Matrix", origin: "Biotransformed Lab Active", type: "Advanced Active", benefit: "Five molecular weights hold 1000x their weight in moisture, at every depth.", pairing: "Synergized with Gotu Kola stem cells", img: LIFE_2 },
  { name: "Ashwagandha (Withania Somnifera)", origin: "Central India", type: "Heritage Botanical", benefit: "Adaptogenic defender against pollution, stress and blue-light fatigue.", pairing: "Complemented by Encapsulated Retinol", img: ING_1 },
  { name: "Encapsulated Salicylic Acid 2%", origin: "Dermatological Grade", type: "Advanced Active", benefit: "Time-released, lipid-soluble pore clearance with minimal irritation.", pairing: "Synergized with Neem Leaf extract", img: SCI_HERO },
];

const TESTIMONIALS = [
  { name: "Ananya Sharma", city: "Mumbai", rating: 5, text: "The Kumkumadi elixir changed my skin in two weeks. My wedding photos needed zero filters — just this ritual.", product: "Kumkumadi Tailam Radiant Elixir" },
  { name: "Dr. Riya Menon", city: "Bengaluru", rating: 5, text: "As a dermatologist I'm picky about actives. The 15% Vitamin C is stabilized properly — elegant formulation, real results.", product: "15% Vitamin C + Ferulic Serum" },
  { name: "Kabir Malhotra", city: "Delhi", rating: 5, text: "Bought the quiz routine for my sister. She made me order my own a week later. The niacinamide elixir is unreal.", product: "10% Niacinamide + Zinc Elixir" },
  { name: "Meera Krishnan", city: "Chennai", rating: 4, text: "The hair ritual oil smells like my childhood Sunday champis, but my hairfall has genuinely halved in a month.", product: "Amla & Bhringraj Hair Ritual Oil" },
];

function CountUp({ to, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 2.2,
      ease: "easeOut",
      onUpdate: (v) => setVal(decimals ? Number(v.toFixed(decimals)) : Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to, decimals]);
  return (
    <span ref={ref} className="font-serif text-3xl sm:text-4xl" style={{ color: "var(--mn-gold)" }}>
      {decimals ? val.toFixed(decimals) : val.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

function RevealLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function useCountdown(target) {
  const calc = () => {
    const diff = Math.max(0, target.getTime() - Date.now());
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff / 3600000) % 24),
      mins: Math.floor((diff / 60000) % 60),
      secs: Math.floor((diff / 1000) % 60),
      live: diff === 0,
    };
  };
  const [t, setT] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return t;
}

export default function Home() {
  const [bestsellers, setBestsellers] = useState([]);
  const [quickView, setQuickView] = useState(null);
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const [activeIng, setActiveIng] = useState(0);
  const carouselRef = useRef(null);
  const heroRef = useRef(null);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  useEffect(() => {
    api.getProducts({ sort: "popular" }).then((d) => setBestsellers(d.slice(0, 8))).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubscribing(true);
    try {
      const res = await api.subscribe(email);
      toast.success(`${res.message} Use code ${res.coupon} for 10% off.`);
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* ============ HERO ============ */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden pt-24" data-testid="hero-section">
        <GradientMesh />
        <Particles count={20} />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div style={{ y: textY }}>
            <motion.p
              className="eyebrow"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              data-testid="hero-eyebrow"
            >
              The House of Mother Nature · Est. Sihor, 2025
            </motion.p>
            <h1 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
              <RevealLine delay={0.35}>
                <span className="gold-text italic">Ancient wisdom.</span>
              </RevealLine>
              <RevealLine delay={0.55}>
                <span style={{ color: "var(--mn-mint)" }}>Modern science.</span>
              </RevealLine>
              <RevealLine delay={0.75}>
                <span style={{ color: "var(--mn-text)" }}>One sacred ritual.</span>
              </RevealLine>
            </h1>
            <motion.p
              className="mt-6 max-w-md text-sm leading-relaxed sm:text-base"
              style={{ color: "var(--mn-text2)" }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.7 }}
            >
              Heritage botanicals and dermatologist-tested, science-backed actives — one house, one shelf, every formula crafted in India for skin that tells the truth.
            </motion.p>
            <motion.div
              className="mt-8 flex flex-wrap items-center gap-4"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.25, duration: 0.7 }}
            >
              <Magnetic>
                <Link to="/shop" data-testid="hero-cta-shop" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
                  <Sparkles size={16} /> Shop the Collection
                </Link>
              </Magnetic>
              <Magnetic>
                <Link to="/shop?sort=popular" data-testid="hero-cta-bestsellers" className="btn-outline-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
                  <Leaf size={16} style={{ color: "var(--mn-gold)" }} /> Bestsellers
                </Link>
              </Magnetic>
              <Link to="/quiz" data-testid="hero-cta-quiz" className="group text-sm underline-offset-4 transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }}>
                Take the Skin Quiz
                <ArrowRight size={13} className="ml-1 inline transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div className="relative mx-auto w-full max-w-sm lg:max-w-md" style={{ y: bottleY }} data-testid="hero-bottle">
            <div className="absolute left-1/2 top-1/2 h-[115%] w-[115%] -translate-x-1/2 -translate-y-1/2 rounded-full animate-pulse-glow" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.22), transparent 60%)" }} />
            <div className="absolute left-1/2 top-1/2 h-[95%] w-[95%] -translate-x-1/2 -translate-y-1/2 animate-spin-slow rounded-full border border-dashed" style={{ borderColor: "var(--mn-glass-border)" }} />
            <motion.div
              animate={{ y: [0, -16, 0], rotate: [0, 1.5, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="relative overflow-hidden border"
              style={{ borderRadius: "999px 999px 32px 32px", borderColor: "var(--mn-glass-border)", boxShadow: "0 30px 80px rgba(0,0,0,0.5)" }}
            >
              <img src={AYU_HERO} alt="Kumkumadi Tailam Radiant Elixir in amber glass" className="aspect-[3/4] w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-1/2 w-[85%] -translate-x-1/2 glass rounded-2xl px-4 py-3">
                <p className="font-mono text-[0.55rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-gold)" }}>Icon Nº 01</p>
                <p className="font-serif text-sm text-white">Kumkumadi Tailam Radiant Elixir</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          style={{ color: "var(--mn-text2)" }}
        >
          <p className="font-mono text-[0.55rem] uppercase tracking-[0.4em]">Scroll the ritual</p>
        </motion.div>
      </section>

      <Marquee />

      {/* ============ UNIFIED SHOWCASE ============ */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="showcase-section">
        <SectionHeading
          eyebrow="One House · Every Ritual"
          title="The forest and the lab, on one shelf."
          sub="No choosing sides. Heritage botanicals and advanced actives live together in every formula we make."
        />
        <Reveal className="relative mt-12 overflow-hidden rounded-[2.5rem]" style={{ border: "1px solid var(--mn-glass-border)" }}>
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="relative min-h-[320px] lg:min-h-[480px]">
              <img src={ING_2} alt="Golden botanicals" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(7,13,9,0.25), var(--mn-bg))" }} />
              <motion.img
                src={SCI_HERO}
                alt="Clinical serum dropper"
                className="absolute bottom-6 right-6 w-32 rounded-2xl border object-cover shadow-2xl sm:w-44"
                style={{ borderColor: "var(--mn-glass-border)", aspectRatio: "3/4" }}
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                loading="lazy"
              />
            </div>
            <div className="flex flex-col justify-center p-8 sm:p-12">
              <p className="font-mono text-[0.62rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-mint)" }}>19 formulas · one philosophy</p>
              <h3 className="mt-4 font-serif text-2xl leading-snug tracking-tight sm:text-3xl lg:text-4xl">
                Kashmiri saffron sits beside <span className="gold-text italic">15% vitamin C.</span> Bhringraj beside <span style={{ color: "var(--mn-mint)" }}>encapsulated retinol.</span>
              </h3>
              <p className="mt-5 max-w-md text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>
                Every Mother Nature formula is a treaty between 5,000 years of herbal intelligence and the precision of the modern lab — blended, buffered and dermatologist tested under one roof.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {["Heritage Botanicals", "Science-Backed Actives", "Dermatologist Tested", "AYUSH Certified"].map((t) => (
                  <span key={t} className="rounded-full border px-3.5 py-1.5 font-mono text-[0.58rem] uppercase tracking-[0.2em]" style={{ borderColor: "var(--mn-glass-border)", color: "var(--mn-text2)" }}>
                    {t}
                  </span>
                ))}
              </div>
              <Magnetic className="mt-8">
                <Link to="/shop" data-testid="showcase-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm">
                  Explore All Formulas <ArrowRight size={15} />
                </Link>
              </Magnetic>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============ DIWALI DROP ============ */}
      <DiwaliSection />

      {/* ============ BESTSELLERS ============ */}
      <section className="py-20 lg:py-24" style={{ background: "var(--mn-bg2)" }} data-testid="bestsellers-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between gap-6">
            <SectionHeading eyebrow="The Icons" title="Bestsellers, loved across India" />
            <div className="hidden gap-2 sm:flex">
              <button data-testid="carousel-prev" onClick={() => carouselRef.current?.scrollBy({ left: -340, behavior: "smooth" })} aria-label="Previous products" className="btn-outline-glow flex h-11 w-11 items-center justify-center rounded-full">
                <ArrowLeft size={16} />
              </button>
              <button data-testid="carousel-next" onClick={() => carouselRef.current?.scrollBy({ left: 340, behavior: "smooth" })} aria-label="Next products" className="btn-outline-glow flex h-11 w-11 items-center justify-center rounded-full">
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
          <div ref={carouselRef} className="no-scrollbar mt-10 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4" data-testid="bestsellers-carousel">
            {bestsellers.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.06} className="w-[270px] shrink-0 snap-start sm:w-[300px]">
                <ProductCard product={p} onQuickView={setQuickView} />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-6 text-center sm:hidden">
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-text2)" }}>Swipe to explore</p>
          </Reveal>
        </div>
      </section>

      {/* ============ SHOP BY CONCERN ============ */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="concerns-section">
        <SectionHeading eyebrow="Skin First" title="Shop by concern" sub="Begin with what your skin is asking for — we'll handle the rest." />
        <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {CONCERNS.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08}>
              <button
                data-testid={`concern-${c.key}`}
                onClick={() => navigate(`/shop?concern=${c.key}`)}
                className="glass-card group flex w-full flex-col items-start rounded-3xl p-6 text-left transition-all duration-500 hover:-translate-y-1.5"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl transition-all duration-500 group-hover:glow-gold" style={{ background: "var(--mn-surface)", color: "var(--mn-gold)" }}>
                  <c.icon size={20} />
                </span>
                <p className="mt-5 font-serif text-lg">{c.label}</p>
                <p className="mt-1 text-xs" style={{ color: "var(--mn-text2)" }}>{c.desc}</p>
                <span className="mt-4 flex items-center gap-1 font-mono text-[0.6rem] uppercase tracking-[0.25em] opacity-0 transition-all duration-500 group-hover:opacity-100" style={{ color: "var(--mn-mint)" }}>
                  Shop now <ArrowRight size={11} />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ INGREDIENT EXPLORER ============ */}
      <section className="relative overflow-hidden py-20 lg:py-28" style={{ background: "var(--mn-bg2)" }} data-testid="ingredient-explorer">
        <Particles count={14} />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <div>
            <SectionHeading eyebrow="Inside the Bottle" title="The ingredient explorer" sub="Every formula is a treaty between the forest and the lab. Tap a node to decode it." />
            <div className="relative mx-auto mt-10 aspect-square max-w-sm" data-testid="ingredient-orbit">
              <div className="absolute inset-0 animate-spin-slow">
                <svg viewBox="0 0 200 200" className="h-full w-full opacity-40">
                  <polygon points="100,20 170,60 170,140 100,180 30,140 30,60" fill="none" stroke="var(--mn-gold)" strokeWidth="0.6" />
                  <polygon points="100,45 148,72 148,128 100,155 52,128 52,72" fill="none" stroke="var(--mn-mint)" strokeWidth="0.5" />
                  <line x1="100" y1="20" x2="100" y2="180" stroke="var(--mn-glass-border)" strokeWidth="0.5" />
                  <line x1="30" y1="60" x2="170" y2="140" stroke="var(--mn-glass-border)" strokeWidth="0.5" />
                  <line x1="170" y1="60" x2="30" y2="140" stroke="var(--mn-glass-border)" strokeWidth="0.5" />
                </svg>
              </div>
              <div className="absolute left-1/2 top-1/2 h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full glow-mint" style={{ background: "radial-gradient(circle, var(--mn-mint), transparent 75%)" }} />
              <div className="absolute left-1/2 top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full glass">
                <Atom size={18} style={{ color: "var(--mn-gold)" }} />
              </div>
              {INGREDIENTS.map((ing, i) => {
                const angle = (i / INGREDIENTS.length) * Math.PI * 2 - Math.PI / 2;
                const x = 50 + Math.cos(angle) * 42;
                const y = 50 + Math.sin(angle) * 42;
                const active = activeIng === i;
                return (
                  <button
                    key={ing.name}
                    data-testid={`ingredient-node-${i}`}
                    onClick={() => setActiveIng(i)}
                    className="absolute flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full glass transition-all duration-500"
                    style={{
                      left: `${x}%`,
                      top: `${y}%`,
                      boxShadow: active ? "0 0 24px rgba(212,175,55,0.55)" : undefined,
                      borderColor: active ? "var(--mn-gold)" : "var(--mn-glass-border)",
                      transform: "translate(-50%,-50%)",
                    }}
                    aria-label={ing.name}
                  >
                    {ing.type.includes("Active") ? (
                      <FlaskConical size={17} style={{ color: "var(--mn-mint)" }} />
                    ) : (
                      <Leaf size={17} style={{ color: "var(--mn-gold)" }} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <Reveal key={activeIng} className="glass-card rounded-3xl p-8" data-testid="ingredient-detail">
            <div className="flex items-start gap-5">
              <img src={INGREDIENTS[activeIng].img} alt={INGREDIENTS[activeIng].name} className="h-24 w-24 shrink-0 rounded-2xl object-cover" loading="lazy" />
              <div>
                <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: INGREDIENTS[activeIng].type.includes("Active") ? "var(--mn-mint)" : "var(--mn-gold)" }}>
                  {INGREDIENTS[activeIng].type}
                </p>
                <h3 className="mt-1.5 font-serif text-xl sm:text-2xl">{INGREDIENTS[activeIng].name}</h3>
                <p className="mt-0.5 font-mono text-[0.65rem]" style={{ color: "var(--mn-text2)" }}>Origin · {INGREDIENTS[activeIng].origin}</p>
              </div>
            </div>
            <p className="mt-6 text-sm leading-relaxed" style={{ color: "var(--mn-text2)" }}>{INGREDIENTS[activeIng].benefit}</p>
            <div className="mt-5 rounded-2xl border p-4" style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-surface)" }}>
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ color: "var(--mn-mint)" }}>Perfect Pairing</p>
              <p className="mt-1 text-sm">{INGREDIENTS[activeIng].pairing}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ STATS ============ */}
      <section className="border-y py-14" style={{ borderColor: "var(--mn-glass-border)" }} data-testid="stats-section">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-8 px-4 text-center sm:px-6 lg:grid-cols-4 lg:px-8">
          {[
            { to: 128, suffix: "K+", label: "Happy Customers" },
            { to: 48, suffix: "K+", label: "Five-Star Reviews" },
            { to: 19, suffix: "", label: "Signature Formulas" },
            { to: 4.8, suffix: "★", label: "Average Rating", decimals: 1 },
          ].map((s) => (
            <div key={s.label}>
              <CountUp to={s.to} suffix={s.suffix} decimals={s.decimals || 0} />
              <p className="mt-2 font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-text2)" }}>{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ RITUAL / BEFORE-AFTER ============ */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="ritual-section">
        <SectionHeading eyebrow="The Ritual" title="Morning gold. Evening molecule." sub="Two timelines, one transformation — visible in 28 days of consistency." />
        <div className="mt-12 grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Reveal className="glass-card group relative overflow-hidden rounded-3xl">
            <img src={LIFE_1} alt="Morning ritual" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-gold)" }}>AM · Awaken</p>
              <h3 className="mt-2 font-serif text-2xl text-white">The Sunrise Ritual</h3>
              <p className="mt-2 max-w-sm text-sm text-white/70">Gentle gel cleanse → 15% Vitamin C → ceramide barrier cream → SPF. Antioxidant armor for the city day ahead.</p>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="glass-card group relative overflow-hidden rounded-3xl">
            <img src={LIFE_2} alt="Evening ritual" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
            <div className="absolute bottom-0 p-7">
              <p className="font-mono text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "var(--mn-mint)" }}>PM · Restore</p>
              <h3 className="mt-2 font-serif text-2xl text-white">The Moonlight Ritual</h3>
              <p className="mt-2 max-w-sm text-sm text-white/70">Double cleanse → encapsulated retinol → Kumkumadi night oil. Cellular renewal while the world sleeps.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ QUIZ BANNER ============ */}
      <section className="relative mx-auto max-w-7xl overflow-hidden rounded-none px-4 py-8 sm:px-6 lg:px-8" data-testid="quiz-banner">
        <Reveal className="glass-card relative overflow-hidden rounded-[2.5rem] px-8 py-14 text-center sm:px-14">
          <GradientMesh />
          <div className="relative">
            <p className="eyebrow">60 seconds · 4 questions</p>
            <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl tracking-tight sm:text-4xl lg:text-5xl">
              Let skin science <span className="gold-text italic">read your skin.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm sm:text-base" style={{ color: "var(--mn-text2)" }}>
              Our diagnostic quiz decodes your concern, skin type and climate into a bespoke ritual — with 15% off your complete bundle.
            </p>
            <Magnetic className="mt-8">
              <Link to="/quiz" data-testid="quiz-banner-cta" className="btn-gold inline-flex items-center gap-2 rounded-full px-8 py-4 text-sm">
                Begin the Diagnosis <ArrowRight size={15} />
              </Link>
            </Magnetic>
          </div>
        </Reveal>
      </section>

      {/* ============ REVIEWS ============ */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28" data-testid="reviews-section">
        <SectionHeading eyebrow="Word of Mouth" title="48,000 five-star mornings" />
        <div className="mt-12 columns-1 gap-5 sm:columns-2 lg:columns-4">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.07} className="mb-5 break-inside-avoid">
              <figure className="glass-card rounded-3xl p-6">
                <Stars value={t.rating} />
                <blockquote className="mt-4 text-sm leading-relaxed" style={{ color: "var(--mn-text)" }}>
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-5 border-t pt-4" style={{ borderColor: "var(--mn-glass-border)" }}>
                  <p className="font-serif text-sm">{t.name} <span className="font-mono text-[0.6rem]" style={{ color: "var(--mn-mint)" }}>· Verified</span></p>
                  <p className="mt-0.5 text-xs" style={{ color: "var(--mn-text2)" }}>{t.city} · on {t.product}</p>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ============ INSTAGRAM ============ */}
      <section className="pb-20 lg:pb-28" data-testid="instagram-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="#MotherNatureGlow" title="The community shelfie" align="center" />
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[AYU_HERO, LIFE_1, SCI_HERO, ING_2, AYU_IMG2, SCI_IMG2].map((src, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <a
                  href="https://instagram.com/mothernature_official"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`instagram-tile-${i}`}
                  className="group relative block overflow-hidden rounded-2xl"
                  aria-label="Instagram post"
                >
                  <img src={src} alt="Mother Nature community post" loading="lazy" className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/55 opacity-0 transition-opacity duration-400 group-hover:opacity-100">
                    <Instagram size={22} className="text-white" />
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ NEWSLETTER ============ */}
      <section className="relative overflow-hidden border-t py-20 lg:py-24" style={{ borderColor: "var(--mn-glass-border)", background: "var(--mn-bg2)" }} data-testid="newsletter-section">
        <Particles count={10} />
        <div className="relative mx-auto max-w-2xl px-4 text-center">
          <Reveal>
            <p className="eyebrow">The Inner Circle</p>
            <h2 className="mt-4 font-serif text-3xl tracking-tight sm:text-4xl">Rituals, launches & <span className="gold-text italic">10% off</span> your first order</h2>
            <p className="mt-3 text-sm" style={{ color: "var(--mn-text2)" }}>One thoughtful letter a week. Never noise.</p>
            <form onSubmit={subscribe} className="mx-auto mt-8 flex max-w-md gap-2" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="glass w-full rounded-full px-5 py-3.5 text-sm outline-none transition-colors focus:border-[var(--mn-gold)] placeholder:text-[var(--mn-text2)]"
              />
              <button data-testid="newsletter-submit" type="submit" disabled={subscribing} className="btn-gold shrink-0 rounded-full px-6 py-3.5 text-sm">
                {subscribing ? "…" : "Join"}
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      <QuickView product={quickView} onClose={() => setQuickView(null)} />
    </div>
  );
}

function DiwaliSection() {
  const t = useCountdown(DIWALI_DATE);
  const [giftSet, setGiftSet] = useState(null);
  const { addToCart } = useStore();
  const navigate = useNavigate();

  useEffect(() => {
    api.getProduct(GIFT_SET_SLUG).then(setGiftSet).catch(() => {});
  }, []);

  const units = [
    { v: t.days, label: "Days" },
    { v: t.hours, label: "Hours" },
    { v: t.mins, label: "Mins" },
    { v: t.secs, label: "Secs" },
  ];

  return (
    <section className="relative overflow-hidden py-20 lg:py-24" style={{ background: "linear-gradient(160deg, #1a0f14 0%, var(--mn-bg2) 45%, #141007 100%)" }} data-testid="diwali-section">
      <Particles count={12} />
      <div className="absolute left-1/2 top-0 h-72 w-[42rem] -translate-x-1/2 rounded-full" style={{ background: "radial-gradient(ellipse, rgba(224,122,95,0.18), transparent 65%)", filter: "blur(50px)" }} />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
        <Reveal>
          <p className="font-mono text-[0.62rem] uppercase tracking-[0.35em]" style={{ color: "var(--mn-terra)" }}>The Festive Drop · Limited Edition</p>
          <h2 className="mt-4 font-serif text-3xl tracking-tight text-[#F4F1EA] sm:text-4xl lg:text-5xl">
            Utsav — the <span className="gold-text italic">Diwali Radiance</span> Gift Set
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed sm:text-base" style={{ color: "#A3B18A" }}>
            Four icons in a hand-block-printed Sanganeri chest, with a brass diya pair and a hand-written card. Only 500 chests — when they're gone, they return next Diwali.
          </p>
          <div className="mt-8 flex gap-3" data-testid="diwali-countdown">
            {units.map((u) => (
              <div key={u.label} className="glass flex h-20 w-20 flex-col items-center justify-center rounded-2xl">
                <span className="font-serif text-2xl" style={{ color: "var(--mn-gold-glow)" }} data-testid={`countdown-${u.label.toLowerCase()}`}>
                  {String(u.v).padStart(2, "0")}
                </span>
                <span className="font-mono text-[0.55rem] uppercase tracking-[0.25em]" style={{ color: "#A3B18A" }}>{u.label}</span>
              </div>
            ))}
          </div>
          <p className="mt-3 font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ color: "#A3B18A" }}>
            {t.live ? "The drop is live now" : "Until the Diwali drop closes · Nov 8"}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {giftSet && (
              <>
                <span className="font-mono text-2xl" style={{ color: "var(--mn-gold-glow)" }} data-testid="diwali-price">
                  {inr(giftSet.price)} <span className="text-sm line-through opacity-50">{inr(giftSet.mrp)}</span>
                </span>
                <Magnetic>
                  <button
                    data-testid="diwali-add-to-cart"
                    onClick={() => { addToCart(giftSet, giftSet.variants[0].label); toast.success("Utsav Gift Set added to your bag"); }}
                    className="btn-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm"
                  >
                    <Gift size={16} /> Reserve Your Chest
                  </button>
                </Magnetic>
              </>
            )}
          </div>
        </Reveal>
        <Reveal delay={0.15} className="relative">
          <div
            className="relative mx-auto max-w-md cursor-pointer overflow-hidden border"
            style={{ borderRadius: "32px 32px 999px 999px", borderColor: "rgba(212,175,55,0.35)", boxShadow: "0 30px 90px rgba(0,0,0,0.55), 0 0 60px rgba(224,122,95,0.15)" }}
            onClick={() => giftSet && navigate(`/product/${giftSet.slug}`)}
            data-testid="diwali-image"
          >
            <img src={giftSet?.images?.[0] || AYU_IMG2} alt="Utsav Diwali Radiance Gift Set" className="aspect-[3/4] w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute left-1/2 top-6 -translate-x-1/2 rounded-full px-4 py-1.5 font-mono text-[0.6rem] uppercase tracking-[0.25em]" style={{ background: "var(--mn-terra)", color: "#1a0f14" }}>
              Only 500 made
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
